/**
 * @file This file contains the AchievementManager class, which handles unlocking achievements.
 * @module AchievementManager
 */

import { achievementsConfig } from '../config/achievementsConfig.js';

export class AchievementManager {
    /**
     * @param {object} dependencies - The dependencies for the manager.
     * @param {import('./PlayerStatsManager.js').PlayerStatsManager} dependencies.playerStatsManager - The player stats manager instance.
     * @param {import('../lib/EventEmitter.js').EventEmitter} dependencies.eventEmitter - The global event emitter.
     * @param {import('./UIManager.js').UIManager} dependencies.ui - The UI manager instance.
     */
    constructor({ playerStatsManager, eventEmitter, ui, audioManager }) {
        /** @private */
        this.playerStatsManager = playerStatsManager;
        /** @private */
        this.eventEmitter = eventEmitter;
        /** @private */
        this.audioManager = audioManager;
        /** @private */
        this.ui = ui;
        /** @private */
        this.achievements = achievementsConfig;
        /** @private */
        this.listeners = {};
        /** @private */
        this.disabled = false;

        this.initListeners();
    }

    /**
     * Sets up event listeners to check for achievement triggers.
     * @private
     */
    initListeners() {
        const createHandler = (handler) => (...args) => !this.disabled && handler.apply(this, args);
        this.listeners['achievement:itemCrafted'] = ({ item, gameState, row, col }) => {
            this.checkItemCraftedAchievements(item, { isCampaignLevel: gameState.isCampaignLevel, activeGameConfig: gameState.activeGameConfig }, row, col);
        };
        this.listeners['achievement:mergeOccurred'] = (payload) => {
            this.checkConsecutiveMergeAchievements(payload.consecutiveCount, { isCampaignLevel: payload.gameState.isCampaignLevel, activeGameConfig: payload.gameState.activeGameConfig }, payload.row, payload.col);
            // A merge occurred, so let's check session and lifetime totals.
            this.checkTotalMergeAchievements(payload.gameState);
        };
        this.listeners['achievement:scoreUpdated'] = ({ newScore, gameState }) => {
            this.checkScoreAchievements(newScore, { isCampaignLevel: gameState.isCampaignLevel });
        };
        this.listeners['achievement:goldUpdated'] = ({ newGoldTotal }) => {
            this.checkGoldAchievements(newGoldTotal);
        };
        this.listeners['achievement:animalCrated'] = ({ gameState }) => {
            this.checkAnimalCratedAchievements(gameState);
        };
        this.listeners['achievement:levelWon'] = ({ gameState, levelIndex }) => {
            this.checkWinConditionAchievements(gameState, levelIndex);
        };
        this.listeners['achievement:gameLost'] = ({ gameState }) => {
            this.checkLoseConditionAchievements(gameState);
        };
        this.listeners['achievement:turnEnded'] = ({ gameState }) => {
            this.checkTurnAchievements(gameState);
        };
        this.listeners['achievement:sessionCrateCountUpdated'] = ({ crateCount, gameState }) => {
            this.checkSessionCrateAchievements(crateCount, gameState);
        };
        this.listeners['achievement:powerupUsed'] = ({ powerupKey, row, col }) => {
            this.checkPowerupUsedAchievements(powerupKey, row, col);
        };
        this.listeners['achievement:perfectPowerup'] = ({ powerupKey, cellsCleared, row, col }) => {
            this.checkPerfectPowerupAchievements(powerupKey, cellsCleared, row, col);
        };
        this.listeners['achievement:disasterSurvived'] = ({ gameState }) => {
            this.checkDisasterAchievements(gameState);
        };

        for (const eventName in this.listeners) {
            this.eventEmitter.on(eventName, createHandler(this.listeners[eventName]));
        }
    }

    /**
     * Removes all event listeners that this instance registered.
     * Essential for cleaning up after tests.
     */
    destroy() {
        for (const eventName in this.listeners) {
            this.eventEmitter.off(eventName, this.listeners[eventName]);
        }
        this.listeners = {};
    }

    /**
     * Disables the achievement manager from processing events.
     */
    disable() {
        this.disabled = true;
    }

    /**
     * Enables the achievement manager to process events.
     */
    enable() {
        this.disabled = false;
    }

    /**
     * Checks for achievements related to crafting a specific item.
     * @param {object} craftedItem - The item that was just crafted.
     * @param {object} gameStateProps - Properties from the current game state.
     * @param {boolean} gameStateProps.isCampaignLevel - Indicates if the current game is a campaign level.
     * @param {object} gameStateProps.activeGameConfig - The active game configuration.
     * @param {number} row - The row where the event occurred.
     * @param {number} col - The column where the event occurred.
     */
    checkItemCraftedAchievements(craftedItem, { isCampaignLevel, activeGameConfig }, row, col) {
        // Achievements can only be earned in linear campaign mode.
        if (!isCampaignLevel || activeGameConfig.gameMode !== 'linear') {
            return;
        }

        // --- Handle ITEM_CRAFTED achievements ---
        for (const key in this.achievements) {
            const achievement = this.achievements[key];
            if (
                achievement.trigger.type !== 'ITEM_CRAFTED' ||
                achievement.trigger.itemName !== craftedItem.name
            ) {
                continue;
            }

            const isUnlocked = this.playerStatsManager.isAchievementUnlocked(key);
            if (isUnlocked && !achievement.showEveryTime) {
                continue;
            }

            const newCount = this.playerStatsManager.incrementAchievementProgress(key);
            const targetCount = achievement.trigger.targetCount || 1;

            if (newCount >= targetCount) {
                this._triggerEffect(key, achievement, isUnlocked, row, col);
            }
        }

        // --- Handle REACH_TIER achievements ---
        // This is checked on any item craft, regardless of whether there's a specific ITEM_CRAFTED achievement for it.
        if (craftedItem.tierIndex !== undefined) {
            const tierIndex = craftedItem.tierIndex || 0; // Assuming tierIndex is on the item
            for (const tierKey in this.achievements) {
                const tierAchievement = this.achievements[tierKey];
                if (tierAchievement.trigger.type === 'REACH_TIER' && !this.playerStatsManager.isAchievementUnlocked(tierKey)) {
                    // Tier is 1-based, tierIndex is 0-based
                    if ((tierIndex + 1) >= tierAchievement.trigger.targetCount) {
                        this._triggerEffect(tierKey, tierAchievement, false, row, col);
                    }
                }
            }
        }
    }

    /**
     * Checks for achievements related to consecutive merges in a single turn.
     * @param {number} consecutiveCount - The number of merges in the current chain reaction.
     * @param {object} gameStateProps - Properties from the current game state.
     * @param {boolean} gameStateProps.isCampaignLevel - Indicates if the current game is a campaign level.
     * @param {object} gameStateProps.activeGameConfig - The active game configuration.
     * @param {number} row - The row where the event occurred.
     * @param {number} col - The column where the event occurred.
     */
    checkConsecutiveMergeAchievements(consecutiveCount, { isCampaignLevel, activeGameConfig }, row, col) {
        // Achievements can only be earned in linear campaign mode.
        if (!isCampaignLevel || activeGameConfig.gameMode !== 'linear') {
            return;
        }

        this._checkTrigger('CONSECUTIVE_MERGES', (achievement, key) => {
            if (consecutiveCount === achievement.trigger.targetCount) {
                const isUnlocked = this.playerStatsManager.isAchievementUnlocked(key);
                this._triggerEffect(key, achievement, isUnlocked, row, col);
            }
        });
    }

    /**
     * Checks for achievements related to total merge counts (session and lifetime).
     * @param {import('../models/GameState.js').GameState} gameState - The current game state.
     */
    checkTotalMergeAchievements(gameState) {
        if (!gameState.isCampaignLevel || gameState.activeGameConfig.gameMode !== 'linear') {
            return;
        }

        // Increment session and lifetime merge counts
        gameState.incrementSessionMerges();
        this.playerStatsManager.incrementTotalMerges();

        this._checkTrigger('TOTAL_MERGES_SESSION', (achievement, key) => {
            if (gameState.getSessionMerges() >= achievement.trigger.targetCount) {
                this._triggerEffect(key, achievement, false, -1, -1);
            }
        });

        this._checkTrigger('TOTAL_MERGES_LIFETIME', (achievement, key) => {
            if (this.playerStatsManager.getTotalMerges() >= achievement.trigger.targetCount) {
                this._triggerEffect(key, achievement, false, -1, -1);
            }
        });
    }

    /**
     * Checks for achievements related to reaching a certain score.
     * @param {number} newScore - The player's new score.
     * @param {object} gameStateProps - Properties from the current game state.
     * @param {boolean} gameStateProps.isCampaignLevel - Indicates if the current game is a campaign level.
     */
    checkScoreAchievements(newScore, { isCampaignLevel }) {
        // Achievements can only be earned in campaign mode.
        if (!isCampaignLevel) {
            return;
        }

        for (const key in this.achievements) {
            const achievement = this.achievements[key];
            const isUnlocked = this.playerStatsManager.isAchievementUnlocked(key);

            if (isUnlocked && !achievement.showEveryTime) {
                continue;
            }

            if (achievement.trigger.type === 'SCORE_SESSION') {
                if (newScore >= achievement.trigger.targetCount) {
                    // For score, we only want to trigger it once per session when the threshold is passed.
                    // We can use the progress tracker for this.
                    if (this.playerStatsManager.getAchievementProgressObject()[key] !== 1) {
                        this.playerStatsManager.incrementAchievementProgress(key); // Mark as achieved for this session.
                        this._triggerEffect(key, achievement, isUnlocked, -1, -1); // Use -1 for non-grid events
                    }
                }
            }
        }
    }

    /**
     * Checks for achievements related to lifetime gold accumulation.
     * @param {number} newGoldTotal - The player's new total gold.
     */
    checkGoldAchievements(newGoldTotal) {
        this._checkTrigger('GOLD_LIFETIME', (achievement, key) => {
            if (newGoldTotal >= achievement.trigger.targetCount) {
                this._triggerEffect(key, achievement, false, -1, -1);
            }
        });
    }

    /**
     * Checks for achievements related to crating an animal for the first time (lifetime).
     * @param {import('../models/GameState.js').GameState} gameState - The current game state.
     */
    checkAnimalCratedAchievements(gameState) {
        if (!gameState.isCampaignLevel) return;

        for (const key in this.achievements) {
            const achievement = this.achievements[key];
            if (achievement.trigger.type === 'ANIMAL_CRATED' && !this.playerStatsManager.isAchievementUnlocked(key)) {
                const newCount = this.playerStatsManager.incrementAchievementProgress(key);
                if (newCount >= achievement.trigger.targetCount) this._triggerEffect(key, achievement, false, -1, -1);
            }
        }
    }

    /**
     * Checks for achievements related to winning a level with specific conditions.
     * @param {import('../models/GameState.js').GameState} gameState - The game state at the moment of winning.
     * @param {number} levelIndex - The index of the level that was won.
     */
    checkWinConditionAchievements(gameState, levelIndex) {
        if (!gameState.isCampaignLevel) return;

        for (const key in this.achievements) {
            const achievement = this.achievements[key];            
            if (achievement.trigger.type !== 'WIN_CONDITION' || this.playerStatsManager.isAchievementUnlocked(key)) {
                continue;
            }

            let conditionsMet = true;
            const trigger = achievement.trigger;

            if (trigger.pathLength && trigger.pathLength !== gameState.activeGameConfig.pathLength) {
                conditionsMet = false;
            }
            if (trigger.gridSize && trigger.gridSize !== gameState.activeGameConfig.gridSize) {
                conditionsMet = false;
            }
            if (trigger.gameMode && trigger.gameMode !== gameState.activeGameConfig.gameMode) {
                conditionsMet = false;
            }
            if (trigger.cratesBuilt === 0 && (gameState.cratesBuilt || 0) > 0) {
                conditionsMet = false;
            }
            if (trigger.levelIndex !== undefined && trigger.levelIndex !== levelIndex) {
                conditionsMet = false;
            }
            if (trigger.powerupsUsed === 0 && (gameState.powerupsUsed || 0) > 0) {
                conditionsMet = false;
            }
            if (trigger.disastersExperienced === 0 && (gameState.disastersSurvived || 0) > 0) {
                conditionsMet = false;
            }

            if (conditionsMet) {
                this._triggerEffect(key, achievement, false, -1, -1);
            }
        }
    }

    /**
     * Checks for achievements related to losing a game with specific conditions.
     * @param {import('../models/GameState.js').GameState} gameState - The game state at the moment of losing.
     */
    checkLoseConditionAchievements(gameState) {
        if (!gameState.isCampaignLevel) return;
        this._checkTrigger('LOSE_CONDITION', (achievement, key) => {
            let conditionsMet = true;
            if (achievement.trigger.score === 0 && gameState.score > 0) {
                conditionsMet = false;
            }
            if (conditionsMet) {
                this._triggerEffect(key, achievement, false, -1, -1);
            }
        });
    }

    /**
     * Checks for achievements related to the number of crates built in a single session.
     * @param {number} crateCount - The current number of crates built in the session.
     * @param {import('../models/GameState.js').GameState} gameState - The current game state.
     */
    checkSessionCrateAchievements(crateCount, gameState) {
        if (!gameState.isCampaignLevel) return;
        for (const key in this.achievements) {
            const achievement = this.achievements[key];
            if (achievement.trigger.type !== 'ANIMAL_CRATED_SESSION') {
                continue;
            }

            const isUnlocked = this.playerStatsManager.isAchievementUnlocked(key);
            if (isUnlocked && !achievement.showEveryTime) {
                continue;
            }
            if (crateCount >= achievement.trigger.targetCount && this.playerStatsManager.getAchievementProgressObject()[key] !== 1) {
                this.playerStatsManager.incrementAchievementProgress(key);
                this._triggerEffect(key, achievement, isUnlocked, -1, -1);
            }
        }
    }

    /**
     * Checks for achievements related to the number of turns taken in a session.
     * @param {import('../models/GameState.js').GameState} gameState - The current game state.
     */
    checkTurnAchievements(gameState) {
        if (!gameState.isCampaignLevel) {
            return;
        }
        for (const key in this.achievements) {
            const achievement = this.achievements[key];
            if (achievement.trigger.type !== 'TURN_COUNT_SESSION' || this.playerStatsManager.isAchievementUnlocked(key)) continue;

            if (gameState.numberOfTurnsTaken >= achievement.trigger.targetCount && this.playerStatsManager.getAchievementProgressObject()[key] !== 1) {
                this.playerStatsManager.incrementAchievementProgress(key);
                this._triggerEffect(key, achievement, false, -1, -1);
            }
        }
    }

    /**
     * Checks for achievements related to using a specific power-up.
     * @param {string} powerupKey - The key of the power-up that was used.
     * @param {number} row - The row where the event occurred.
     * @param {number} col - The column where the event occurred.
     */
    checkPowerupUsedAchievements(powerupKey, row, col) {
        this._checkTrigger('POWERUP_USED', (achievement, key) => {
            if (achievement.trigger.powerupKey !== powerupKey) {
                return;
            }
            const newCount = this.playerStatsManager.incrementAchievementProgress(key);
            if (newCount >= achievement.trigger.targetCount) {
                this._triggerEffect(key, achievement, false, row, col);
            }
        });
    }

    /**
     * Checks for achievements related to perfect power-up usage.
     * @param {string} powerupKey - The key of the power-up that was used.
     * @param {number} cellsCleared - The number of cells cleared by the power-up.
     * @param {number} row - The row where the event occurred.
     * @param {number} col - The column where the event occurred.
     */
    checkPerfectPowerupAchievements(powerupKey, cellsCleared, row, col) {
        this._checkTrigger('PERFECT_POWERUP', (achievement, key) => {
            if (achievement.trigger.powerupKey !== powerupKey) {
                return;
            }
            if (cellsCleared >= achievement.trigger.cellsCleared) {
                this._triggerEffect(key, achievement, false, row, col);
            }
        });
    }

    /**
     * Checks for achievements related to surviving disasters.
     * @param {import('../models/GameState.js').GameState} gameState - The current game state.
     */
    checkDisasterAchievements(gameState) {
        if (!gameState.isCampaignLevel) {
            return;
        }

        gameState.incrementDisastersSurvived();
        this.playerStatsManager.incrementTotalDisastersSurvived();

        for (const key in this.achievements) {
            const achievement = this.achievements[key];
            const isUnlocked = this.playerStatsManager.isAchievementUnlocked(key);

            if (isUnlocked && !achievement.showEveryTime) {
                continue;
            }

            let trigger = false;
            switch (achievement.trigger.type) {
            case 'DISASTER_SURVIVED_SESSION':
                if (gameState.disastersSurvived >= achievement.trigger.targetCount) {
                    // Use progress tracker to fire once per session.
                    if (this.playerStatsManager.getAchievementProgressObject()[key] !== 1) {
                        this.playerStatsManager.incrementAchievementProgress(key);
                        trigger = true;
                    }
                }
                break;
            case 'DISASTER_SURVIVED_LIFETIME':
                const newLifetimeCount = this.playerStatsManager.incrementAchievementProgress(key);
                if (newLifetimeCount >= achievement.trigger.targetCount) {
                    trigger = true;
                }
                break;
            }
            if (trigger) { this._triggerEffect(key, achievement, isUnlocked, -1, -1); }
        }
    }

    /**
     * A generic helper to loop through achievements and check a specific trigger type.
     * @param {string} triggerType - The achievement trigger type to check for.
     * @param {function(object, string): void} checkerFn - The function to run for each matching achievement.
     * @private
     */
    _checkTrigger(triggerType, checkerFn) {
        for (const key in this.achievements) {
            const achievement = this.achievements[key];
            if (achievement.trigger.type === triggerType) {
                if (this.playerStatsManager.isAchievementUnlocked(key) && !achievement.showEveryTime) {
                    continue;
                }
                checkerFn(achievement, key);
            }
        }
    }

    /**
     * Decides whether to unlock an achievement or just show the notification.
     * @private
     */
    _triggerEffect(key, achievement, isUnlocked, row, col) {
        if (!isUnlocked) {
            this._unlockAndNotify(key, achievement, row, col);
        } else {
            this._showNotification(achievement, row, col);
        }
    }

    /**
     * Handles the logic for unlocking an achievement and triggering all associated feedback.
     * @param {string} key - The achievement key.
     * @param {object} achievement - The achievement configuration object.
     * @param {number} row - The row where the event occurred.
     * @param {number} col - The column where the event occurred.
     * @private
     */
    _unlockAndNotify(key, achievement, row, col) {
        this.playerStatsManager.unlockAchievement(key);
        this._showNotification(achievement, row, col);
    }

    /**
     * Triggers the visual and audio feedback for an achievement.
     * @param {object} achievement - The achievement configuration object.
     * @param {number} row - The row where the event occurred.
     * @param {number} col - The column where the event occurred.
     * @private
     */
    _showNotification(achievement, row, col) {
        this.audioManager.play('achievement');
        if (row !== -1 && col !== -1) {
            this.ui.addTemporaryEffect(row, col, 'achievementUnlock');
        }
        this.ui.showAchievementToast(achievement);
    }
}